import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject } from "rxjs";
import { lastValueFrom } from "rxjs";
import { ApiUrlService } from "./api-url.service";

export interface OrgSwitcherEntry {
  organization_id: string;
  organization_name: string | null;
  roles: string[];
  active: boolean;
  requires_account_switch: boolean;
}

export interface OrgSwitcherAccount {
  account_id: string;
  email: string;
  orgs: OrgSwitcherEntry[];
}

export interface OrgSwitcherData {
  current_account: OrgSwitcherAccount;
  linked_accounts: OrgSwitcherAccount[];
}

@Injectable({ providedIn: "root" })
export class ActiveOrganizationService {
  private data$ = new BehaviorSubject<OrgSwitcherData | null>(null);

  readonly orgs$ = this.data$.asObservable();

  constructor(private http: HttpClient, private apiUrlService: ApiUrlService) {}

  getValue(): OrgSwitcherData | null {
    return this.data$.getValue();
  }

  getActiveOrg(): OrgSwitcherEntry | null {
    const data = this.data$.getValue();
    if (!data) return null;
    return data.current_account.orgs.find((org) => org.active) ?? null;
  }

  async switchActiveOrganization(organizationId: string): Promise<boolean> {
    try {
      await lastValueFrom(
        this.http.put(
          `${this.apiUrlService.getApiUrl()}/api/users/me/active-organization`,
          { organization_id: organizationId }
        )
      );
      await this.refresh();
      return true;
    } catch (err) {
      console.error(
        "ActiveOrganizationService failed to switch organization:",
        err
      );
      return false;
    }
  }

  async refresh(): Promise<OrgSwitcherData | null> {
    try {
      const response = await lastValueFrom(
        this.http.get<OrgSwitcherData>(
          `${this.apiUrlService.getApiUrl()}/api/users/me/organizations/all`
        )
      );
      this.data$.next(response);
      return response;
    } catch (err) {
      console.error(
        "ActiveOrganizationService failed to load organizations:",
        err
      );
      return null;
    }
  }

  clear(): void {
    this.data$.next(null);
  }
}
